import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  BookOpen, 
  ClipboardList,
  Truck,
  CalendarDays,
  CalendarCheck,
  LogOut,
  Menu,
  X,
  ChefHat,
  Inbox
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';

const StaffLayout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('unauthenticated');
      const data = await res.json();
      if (!['bakery_staff', 'admin'].includes(data.role)) {
        navigate('/staff/login');
        return;
      }
      const staffUser = { ...data, username: data.contact_name || data.email.split('@')[0] };
      localStorage.setItem('staff_user', JSON.stringify(staffUser));
      setUser(staffUser);
      setIsReady(true);
    } catch {
      localStorage.removeItem('staff_user');
      navigate('/staff/login');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore network errors on logout
    }
    localStorage.removeItem('staff_user');
    navigate('/staff/login');
  };

  const navItems = [
    { path: '/staff/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/staff/schedule', icon: CalendarCheck, label: 'My Schedule' },
    { path: '/staff/requests', icon: Inbox, label: 'My Requests' },
    { path: '/staff/menu-calendar', icon: CalendarDays, label: 'Menu Calendar' },
    { path: '/staff/inventory', icon: Package, label: 'Inventory' },
    { path: '/staff/recipes', icon: BookOpen, label: 'Recipes' },
    { path: '/staff/production', icon: ClipboardList, label: 'Production' },
    { path: '/staff/delivery', icon: Truck, label: 'Delivery' },
  ];

  if (!isReady) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading kitchen portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile header */}
      <div className="lg:hidden bg-slate-800 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="h-6 w-6 text-orange-400" />
          <span className="font-bold">MAD Donuts Kitchen</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="[&_button]:text-white [&_button:hover]:bg-slate-700 [&_svg]:text-white">
            <NotificationBell />
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-700"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky inset-y-0 left-0 lg:top-0 z-50
          w-64 bg-slate-800 text-white
          h-screen lg:h-screen
          flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Logo */}
          <div className="p-6 border-b border-slate-700 hidden lg:block flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <ChefHat className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">MAD Donuts</h1>
                <p className="text-xs text-slate-400">Kitchen Portal</p>
              </div>
            </div>
          </div>

          {/* Navigation — scrollable region */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-orange-500 text-white' 
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'}
                `}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section pinned at bottom */}
          <div className="p-4 border-t border-slate-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'S'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium">{user?.username || 'Staff'}</p>
                  <p className="text-xs text-slate-400">Kitchen Staff</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                data-testid="logout-button"
                aria-label="Logout"
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content — min-w-0 so wide tables/grids scroll inside their own
            container instead of stretching the page sideways on mobile. */}
        <main className="flex-1 min-w-0 min-h-screen">
          {/* Desktop top bar with NotificationBell (mobile shows it in the header above) */}
          <div className="hidden lg:flex items-center justify-end px-6 lg:px-8 py-3 bg-white border-b border-slate-200">
            <NotificationBell />
          </div>
          <div className="p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default StaffLayout;
